import React, { useEffect, useState } from "react";
import { Link } from "gatsby";
import {
  ArrowRight,
  BadgeCheck,
  BarChart3,
  Check,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  ClipboardList,
  FolderOpen,
  LockKeyhole,
  Mail,
  Phone,
  Search,
  ShieldCheck,
  Smile,
  Sparkles,
  Users,
  X,
} from "lucide-react";
import ContactForm from "./ContactForm";
import RoofingCalculator from "./sidebars/roof-calc";
import LocationsMap from "./locations-map";
import siteConfig from "../siteConfig";
import {
  BBB_PROFILE,
  GAF_PROFILE,
  GOOGLE_REVIEWS,
  HOUZZ_PROFILE,
  YELP_PROFILE,
} from "../reviewProfiles";
import "../styles/roach-homepage.css";

const asset = (name) => `/assets/roach-homepage/${name}`;

const heroServices = ["Roofing Repair", "Roofing Replacement", "Roofing Inspection"];

const reviewPlatforms = [
  { name: "Yelp", image: "yelp-reviews-92.webp", width: 129, height: 92, rating: "4/5 Rating", href: YELP_PROFILE },
  { name: "Google", image: "google-reviews-92.webp", width: 166, height: 92, rating: "4.9/5 Rating", href: GOOGLE_REVIEWS },
  { name: "Facebook", image: "facebook-reviews-92.webp", width: 166, height: 92, rating: "5/5 Rating" },
  { name: "Angi", image: "angi-reviews-92.webp", width: 166, height: 92, rating: "4/5 Rating" },
  { name: "Houzz", image: "houzz-reviews-92.webp", width: 166, height: 92, rating: "5/5 Rating", href: HOUZZ_PROFILE },
  { name: "BBB", image: "bbb-accredited-business-92.webp", width: 152, height: 92, rating: "A+ Rated Rating", href: BBB_PROFILE },
];

const offices = [
  { city: "Battle Creek", address: "901 Capital Ave Northeast", detail: "Battle Creek, Michigan" },
  { city: "Jackson", address: "2600 Airport Rd. Suite 100", detail: "Jackson MI 49202" },
  { city: "Wyoming", address: "4331 Clyde Park Ave. SW", detail: "Wyoming, MI 49509" },
];

// Locations shown on the homepage map + cards (Figma comment 73). Kept separate
// from `offices` above so the existing "3 Local Offices" section is unchanged.
const locations = [
  {
    city: "Battle Creek",
    tag: "Headquarters",
    address: "901 Capital Ave NE",
    detail: "Battle Creek, MI 49017",
    phone: "(269) 420-6623",
    page: "/roofing-battle-creek-mi/",
    lat: 42.3342,
    lng: -85.1584,
  },
  {
    city: "Wyoming",
    tag: "Grand Rapids area",
    address: "4331 Clyde Park Ave SW",
    detail: "Wyoming, MI 49509",
    phone: "(616) 367-3700",
    page: "/roofing-wyoming/",
    lat: 42.8797,
    lng: -85.68,
  },
  {
    city: "Jackson",
    address: "2600 Airport Rd, Suite 100",
    detail: "Jackson, MI 49202",
    phone: "(517) 544-0401",
    page: "/roofing-jackson-mi/",
    lat: 42.2601,
    lng: -84.459,
  },
];

// Google Maps "directions to" deep link for a given location address.
const directionsUrl = (loc) =>
  `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(
    `${loc.address}, ${loc.detail}`
  )}`;

// Communities in the "Some Communities We Serve" list that have their own
// roofing page, so those names can link out (Figma comment / request #3).
const communityPages = {
  Kalamazoo: "/roofing-kalamazoo-mi/",
  Portage: "/roofing-portage-mi/",
  Richland: "/roofing-richland-mi/",
  Vicksburg: "/roofing-vicksburg-mi/",
  Plainwell: "/roofing-plainwell-mi/",
  Delton: "/roofing-delton-mi/",
  Galesburg: "/roofing-galesburg-mi/",
  Marshall: "/roofing-marshall-mi/",
  Bellevue: "/roofing-bellevue/",
  Athens: "/roofing-athens-mi/",
  Charlotte: "/roofing-charlotte-mi/",
  Jackson: "/roofing-jackson-mi/",
  Grandville: "/roofing-grandville-mi/",
  Wyoming: "/roofing-wyoming/",
  Ada: "/roofing-ada-mi/",
};

const services = [
  {
    title: "Residential and Commercial Roofing",
    description:
      "Roofing done right the first time. We install and repair asphalt shingle, tile, standing seam metal, and flat roofing systems built for Michigan's freeze thaw cycles, heavy snow load, and storm damage. Every job includes a full inspection, a written estimate, and a workmanship guarantee.",
    image: "roofing-service-370.webp",
    alt: "New asphalt shingle roof overlooking a Michigan neighborhood",
    href: "/roofing/",
    cta: "Explore Roofing Services",
  },
  {
    title: "Siding Installation",
    description:
      "Siding built for Michigan weather. We install vinyl and fiber cement siding designed to hold up against wind, hail, and temperature swings, with clean trim work and proper moisture barriers so your home stays protected and looks sharp for decades.",
    image: "siding-service-370.webp",
    alt: "Michigan home with professionally installed siding",
    href: "/siding/",
    cta: "Explore Siding Services",
  },
  {
    title: "Premium Windows and Doors",
    description:
      "Windows and doors that actually perform. We install Pella and Polaris windows and doors, chosen for their energy efficiency and durability in Michigan winters. Every installation is sealed and finished to manufacturer spec, backed by our workmanship guarantee.",
    image: "windows-doors-service-370.webp",
    alt: "Bright home interior with replacement windows and doors",
    href: "/windows-doors/",
    cta: "Explore Windows and Doors",
  },
];

const testimonials = [
  {
    title: "We suffered significant roof and gutter damage from a storm",
    body: "Roach Home Improvement worked with our insurance company and made the paperwork pretty painless. The crew came in and installed our new roof in a single day.",
    author: "Kevin Rabineau",
  },
  {
    title: "Definitely recommend Roach! Such a good experience",
    body: "They were super nice and they didn't mind getting more into detail because I needed a little more assurance after what I went through with another company.",
    author: "Nic Liceaga",
  },
  {
    title: "100% call backs, information asap, and they show up on time",
    body: "The best place I've ever dealt with! We had different crews come for our home work and every time they were polite, respectful and cleaned up like I'd want.",
    author: "Wesley Fry",
  },
];

// `thumb` (640px WebP) feeds the grid; the full-size `src` is only fetched when
// the lightbox opens.
const projects = [
  { src: asset("project-01.jpg"), thumb: asset("project-01-640.webp"), alt: "Completed Roach Home Improvement project at a Michigan home" },
  { src: asset("project-02.jpg"), thumb: asset("project-02-640.webp"), alt: "Roach Home Improvement office and wrapped company vehicle" },
  { src: asset("project-03.jpg"), thumb: asset("project-03-640.webp"), alt: "Aerial view of a completed residential roof replacement" },
  { src: asset("project-04.jpg"), thumb: asset("project-04-640.webp"), alt: "Roach roofing crew working on a home during winter" },
  { src: asset("project-05.jpg"), thumb: asset("project-05-640.webp"), alt: "Michigan home with a completed Roach roof installation" },
  { src: asset("project-06.jpg"), thumb: asset("project-06-640.webp"), alt: "Completed roofing and exterior project on a Michigan home" },
  { src: asset("project-07.jpg"), thumb: asset("project-07-640.webp"), alt: "Rear exterior of a completed residential roofing project" },
  { src: asset("project-08.jpg"), thumb: asset("project-08-640.webp"), alt: "Roach Home Improvement roadside billboard" },
  { src: asset("project-09.jpg"), thumb: asset("project-09-640.webp"), alt: "Roach Home Improvement office and company trucks" },
  { src: asset("project-10.jpg"), thumb: asset("project-10-640.webp"), alt: "Roach Home Improvement truck and project sign at a job site" },
];

const insuranceHighlights = [
  {
    title: "Storm response",
    text: "Emergency inspections within 24 to 48 hours of contact, with tarping available to prevent interior damage while you wait on approval.",
  },
  {
    title: "Adjuster meetings",
    text: "We walk the roof with your adjuster so the damage assessment matches what we're actually seeing.",
  },
  {
    title: "Proper permitting",
    text: "Full permits and documentation on every job, since unpermitted work can get a claim denied.",
  },
  {
    title: "Licensed and insured",
    text: "Michigan license #2101200181, with liability and workers' compensation through Frankenmuth Insurance.",
  },
];

const whyRoach = [
  {
    icon: FolderOpen,
    title: "Local Experience & Knowledge",
    text: "We’re familiar with the local climate, building codes, weather challenges (snow, storms, seasonal shifts).",
  },
  {
    icon: FolderOpen,
    title: "Transparent Quotes",
    text: "We provides free, no-obligation estimates so you can see the cost ahead of time.",
  },
  {
    icon: FolderOpen,
    title: "Licensed And Insured",
    text: "We are fully licensed in Michigan, with liability and workers compensation insurance on every job.",
  },
  {
    icon: BarChart3,
    title: "Quality Materials & Installation",
    text: "We partner with good manufacturers (e.g. GAF shingles) and use materials built to withstand Michigan’s weather.",
  },
  {
    icon: Search,
    title: "Warranty & Guarantees",
    text: "We offer manufacturer warranties and our own workmanship guarantee.",
  },
  {
    icon: BadgeCheck,
    title: "Customer-Focused Service",
    text: "Clean-up, respect for the property (landscaping, etc.), good communication.",
  },
];

const roofingServices = [
  {
    title: "Tile Roofing Installation & Repair",
    text: "We install and repair clay and concrete tile roofs, a system built for homes that need decades of weather resistance. Get a free estimate before you decide.",
  },
  {
    title: "Roof Repair and Restoration",
    text: "Leaks, missing shingles, and storm damage don't wait. We diagnose the cause first, then repair or restore so the same problem doesn't come back next season.",
  },
  {
    title: "Owens Corning Shingle Roofing Systems",
    text: "Certified Owens Corning installers using their full shingle lineup, backed by manufacturer warranties that only certified contractors can offer.",
  },
  {
    title: "High Efficacy Mechanically Seamed Metal Roofs",
    text: "Standing-seam metal roofing installed with mechanical seaming, not just crimping. Handles Michigan’s freeze-thaw cycles and heavy snow load on homes and commercial buildings alike.",
  },
  {
    title: "Custom Roofing Projects",
    text: "Steep pitches, dormers, turrets, or anything off a standard spec sheet. We work from your architect's drawings or build the plan with you on site.",
  },
  {
    title: "Advanced Flat Roofing Systems",
    text: "TPO and EPDM flat roof installation for commercial and low-slope residential properties, sealed for standing water and foot traffic.",
  },
];

const processSteps = [
  {
    title: "Request a Free Consultation",
    text: "Call, text, or fill out the online form. We schedule an on-site inspection, usually within 48 hours, and check your roof's condition, age, and any storm or leak damage before writing a quote.",
  },
  {
    title: "A Plan Built for Your Roof",
    text: "We go over material options (CertainTeed, Owens Corning, standing seam metal), your budget, and your timeline. If insurance is covering the work, we handle the claim paperwork and coordinate directly with your adjuster.",
  },
  {
    title: "Time To Work",
    text: "Our crew arrives with everything needed to complete most residential roofs in one to two days. We clean up the site fully and walk the finished roof with you before we consider the job done.",
  },
];

const communities = [
  ["Kalamazoo", "49001, 49003–49009, 49048"],
  ["Portage", "49002, 49024, 49081"],
  ["East Leroy", "49051"],
  ["Richland", "49083"],
  ["Vicksburg", "49097"],
  ["Plainwell", "49080"],
  ["Delton", "49046"],
  ["Climax", "49034"],
  ["Galesburg", "49053"],
  ["Marshall", "49068"],
  ["Bellevue", "49021"],
  ["Bedford", "49020"],
  ["Athens", "49011"],
  ["Charlotte", "48813"],
  ["Hickory Corners", "49060"],
  ["Augusta", "49012"],
  ["Coldwater", "49036"],
  ["Jackson", "49201, 49202, 49203"],
  ["Grand Rapids", "49503, 49504, 49505, 49507, 49512"],
  ["Kentwood", "49508, 49512, 49518, 49548"],
  ["Grandville", "49418"],
  ["Wyoming", "49509, 49519"],
  ["Ada", "49301"],
  ["Hudsonville", "49426"],
];

const faqs = [
  ["How long does a roof replacement take?", "Most residential roof replacements are completed in one to two days, depending on size, pitch, layers, decking repairs, and weather."],
  ["Do you remove old roofing or install over the existing layer?", "We recommend removing the old roof so the decking can be inspected and the new system can be installed to manufacturer requirements."],
  ["What warranty comes with my new roof?", "Warranty coverage depends on the selected material and system. We review both manufacturer and workmanship coverage before work begins."],
  ["How long will my roof actually last in Michigan's climate?", "A properly installed and ventilated roof can perform for decades. Material, exposure, maintenance, and storm history all affect service life."],
  ["Do I need to be home during the installation?", "No. We confirm access, parking, pets, and any special instructions before the crew arrives, then keep you updated throughout the project."],
  ["How often should I have my roof inspected in West Michigan?", "Schedule an inspection annually and after major wind, hail, or ice events so small issues can be addressed early."],
  ["How much does a roof replacement cost in West Michigan, MI?", "Pricing depends on roof size, pitch, tear-off, decking, ventilation, flashing, and material selection. A site inspection provides an accurate estimate."],
  ["Do you offer emergency roof repair in Kalamazoo and the surrounding area?", "Yes. Contact us for urgent leak and storm-damage inspections, including temporary protection when conditions allow."],
];

function EstimateCard() {
  const [selected, setSelected] = useState(heroServices[0]);

  const continueToForm = () => {
    if (typeof document !== "undefined") {
      document.getElementById("estimate")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <aside className="rh-estimate-card" aria-label="Free estimate">
      <div className="rh-estimate-offer">
        <strong>$500 Off</strong>
        <span>Plus 50% Off Gutters With Roof Replacement</span>
        <small>Minimum 20 square roof to qualify.</small>
      </div>
      <div className="rh-estimate-card__body">
        <span className="rh-special-label">Online Special</span>
        <h2>Get a Free Estimate</h2>
        <p>FREE same day comprehensive estimates on all projects.</p>
        <div className="rh-service-options" role="radiogroup" aria-label="Select a roofing service">
          {heroServices.map((service) => (
            <button
              type="button"
              role="radio"
              aria-checked={selected === service}
              className={selected === service ? "is-selected" : ""}
              key={service}
              onClick={() => setSelected(service)}
            >
              <span className="rh-service-option__icon" aria-hidden="true">🏠</span>
              <span>{service}</span>
              {selected === service ? <Check size={20} aria-hidden="true" /> : null}
            </button>
          ))}
        </div>
        <button type="button" className="rh-button rh-button--dark rh-button--full" onClick={continueToForm}>
          Next
        </button>
        <p className="rh-privacy"><LockKeyhole size={15} aria-hidden="true" /> Your privacy is important to us.</p>
      </div>
    </aside>
  );
}

function RoachHomepage() {
  const [lightbox, setLightbox] = useState(null);
  const [videoPlaying, setVideoPlaying] = useState(false);

  useEffect(() => {
    if (lightbox === null) return undefined;

    const previousOverflow = document.body.style.overflow;
    const handleKeyDown = (event) => {
      if (event.key === "Escape") setLightbox(null);
      if (event.key === "ArrowLeft") {
        setLightbox((current) => (current - 1 + projects.length) % projects.length);
      }
      if (event.key === "ArrowRight") {
        setLightbox((current) => (current + 1) % projects.length);
      }
    };

    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [lightbox]);

  return (
    <main className="rh-homepage">
      <div className="rh-hero-lead-in" aria-hidden="true" />

      <section className="rh-hero" aria-labelledby="rh-hero-title">
        <div className="rh-container rh-hero__layout">
          <div className="rh-hero__content">
            <div className="rh-hero-ratings" aria-label="Top review ratings">
              <a
                className="rh-hero-rating"
                href={GOOGLE_REVIEWS}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Read Roach Home Improvement reviews on Google"
              >
                <b>G</b> ⭐⭐⭐⭐⭐<small>5/5 Rating</small>
              </a>
              <span className="rh-hero-rating"><b>A</b> ⭐⭐⭐⭐⭐<small>5/5 Rating</small></span>
              <span className="rh-hero-rating"><b>♛</b> ⭐⭐⭐⭐<small>4/5 Rating</small></span>
            </div>
            <h1 id="rh-hero-title">
              The Big Name in Roofing:
              <span>Trusted Roofing Contractor in West Michigan</span>
            </h1>
            <p>
              Roach Home Improvement provides roof repair and replacement services in Battle Creek,
              Wyoming, Jackson, and nearby communities for 20+ years. We are GAF Licensed and insured.
            </p>
            <a className="rh-button rh-button--dark" href="tel:+12694206623">(269) 420-6623</a>
            <div className="rh-hero-badges">
              <a href={BBB_PROFILE} target="_blank" rel="noopener noreferrer" aria-label="View Roach Home Improvement on BBB">
                <img src={asset("bbb-a-plus-rating-160.webp")} alt="BBB accredited A plus rating" width="160" height="140" />
              </a>
              <a href={GAF_PROFILE} target="_blank" rel="noopener noreferrer" aria-label="View Roach Home Improvement's GAF profile">
                <img src={asset("gaf-master-elite-contractor-240.webp")} alt="GAF Master Elite contractor" width="240" height="240" />
              </a>
            </div>
          </div>
          <EstimateCard />
        </div>
      </section>

      <section className="rh-premier-bar" aria-label="Premier roofing contractor benefits">
        <div className="rh-container">
          <h2>#1 Premier Roofing Contractor</h2>
          <div className="rh-premier-points">
            <span><BadgeCheck /> Licensed and Insured</span>
            <span><Search /> Free Estimates and Inspections</span>
            <span><ShieldCheck /> We Work With Your Insurance</span>
            <span><ClipboardList /> Competitive Financing</span>
          </div>
        </div>
      </section>

      <section className="rh-review-platforms" aria-label="Review profiles">
        <div className="rh-container rh-review-platforms__grid">
          {reviewPlatforms.map((platform) =>
            platform.href ? (
              <a
                className="rh-review-platform is-clickable"
                key={platform.name}
                href={platform.href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`View Roach Home Improvement on ${platform.name}`}
              >
                <img src={asset(platform.image)} alt={`${platform.name} reviews`} width={platform.width} height={platform.height} loading="lazy" />
                <span>{platform.rating}</span>
              </a>
            ) : (
              <div className="rh-review-platform" key={platform.name}>
                <img src={asset(platform.image)} alt={`${platform.name} reviews`} width={platform.width} height={platform.height} loading="lazy" />
                <span>{platform.rating}</span>
              </div>
            )
          )}
        </div>
      </section>

      <section className="rh-office-intro">
        <div className="rh-container rh-office-intro__inner">
          <h2>Serving West Michigan From 3 Local Offices</h2>
          <div className="rh-office-grid">
            {offices.map((office) => (
              <article key={office.city}>
                <h3>{office.city}</h3>
                <p>{office.address}<br />{office.detail}</p>
              </article>
            ))}
          </div>
          <div className="rh-office-detail">
            <div>
              <h3>Direct Contact</h3>
              <p><b>Phone:</b> <a href={`tel:${siteConfig.phoneE164(siteConfig.phone)}`}>{siteConfig.phone}</a><br /><b>Availability:</b> Same-Day Inspections, 24/7 Emergency Response</p>
            </div>
            <div>
              <h3>Our Roofing License</h3>
              <p><b>License No:</b> 2101200181<br />BBB Accredited, A+ Rating</p>
            </div>
          </div>
        </div>
      </section>

      <section className="rh-about">
        <div className="rh-container">
          <h2 className="rh-section-title">Your Local Roofing &amp; Home Improvement Contractor in West Michigan</h2>
          <div className="rh-about__grid">
            <div className="rh-about__copy">
              <p>I started Roach Home Improvement because homeowners deserve clear pricing and work that lasts. That’s why families across Battle Creek, Wyoming, Jackson, and the surrounding areas call us first.</p>
              <p><strong><em>Shaun Roach, CEO of Roach Home Improvement</em></strong></p>
              <ul>
                <li><strong>Straightforward Pricing</strong> — Clear estimates before we start. No hidden fees, no surprise costs.</li>
                <li><strong>Licensed and Insured</strong> — Every job backed by years of local roofing and home improvement experience.</li>
                <li><strong>Trusted Materials</strong> — CertainTeed roofing, Pella and Polaris windows and doors, installed to last.</li>
                <li><strong>Storm Damage and Insurance Help</strong> — We handle inspections, paperwork, and insurance claims for you.</li>
                <li><strong>271 Five-Star Reviews</strong> — A track record built job by job across West Michigan.</li>
              </ul>
              <a className="rh-button rh-button--red" href="#estimate">Schedule a FREE Consultation</a>
            </div>
            <div className="rh-about__video">
              {videoPlaying ? (
                <iframe
                  className="rh-about__video-frame"
                  src="https://www.youtube.com/embed/NZA-mxnVO0E?autoplay=1&rel=0&modestbranding=1"
                  title="Shaun Roach introduces Roach Home Improvement"
                  loading="lazy"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  referrerPolicy="strict-origin-when-cross-origin"
                  allowFullScreen
                />
              ) : (
                <button
                  type="button"
                  className="rh-about__video-trigger"
                  onClick={() => setVideoPlaying(true)}
                  aria-label="Play video: Shaun Roach introduces Roach Home Improvement"
                >
                  <img
                    src={asset("shaun-roach-video-poster-1032.webp")}
                    alt="Shaun Roach beside a Roach Home Improvement truck"
                    loading="lazy"
                    width="1032"
                    height="526"
                  />
                  <span className="rh-about__play" aria-hidden="true">▶</span>
                </button>
              )}
            </div>
          </div>
          <div className="rh-stats">
            <div><strong>25 Years</strong><span>Leak Warranty</span></div>
            <div><strong>5K+</strong><span>Roofs Replaced</span></div>
            <div><strong>20 Years</strong><span>Serving Communities</span></div>
          </div>
        </div>
      </section>

      <section className="rh-accreditations" aria-label="Accreditations and certifications">
        <div className="rh-container">
          <img src="/assets/logos/products/CHAMBERS-OF-COMMERCE-160.webp" alt="Battle Creek Area Chamber accredited" loading="lazy" width="86" height="160" />
          <img src="/assets/logos/products/Seal_of_the_United_States_Environmental_Protection_Agency.svg" alt="EPA Lead Safe certified firm" loading="lazy" />
          <img src="/assets/logos/products/MS-AuthorizedContractor-Badge-184.webp" alt="Michigan Saves authorized contractor" loading="lazy" width="184" height="184" />
          <img src="/assets/logos/products/hba-michigan-min.svg" alt="Home Builders Association of Michigan" loading="lazy" />
          <a href={BBB_PROFILE} target="_blank" rel="noopener noreferrer">
            <img src="/assets/logos/products/bbb.svg" alt="BBB accredited business" loading="lazy" />
          </a>
        </div>
      </section>

      <section className="rh-services" id="services">
        <div className="rh-container">
          <h2 className="rh-section-title">Services We Provide Throughout West Michigan</h2>
          <div className="rh-services__list">
            {services.map((service, index) => (
              <article className={index % 2 === 1 ? "is-reversed" : ""} key={service.title}>
                <div className="rh-services__copy">
                  <h3>{service.title}</h3>
                  <p>{service.description}</p>
                  <Link className="rh-button rh-button--dark" to={service.href}>{service.cta}</Link>
                </div>
                <img src={asset(service.image)} alt={service.alt} loading="lazy" />
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="rh-testimonials" id="reviews">
        <div className="rh-container">
          <h2 className="rh-section-title">Read What Our Clients Say About Us</h2>
          <div className="rh-testimonials__grid">
            <div className="rh-review-summary">
              <strong>4.9<span>/5</span></strong>
              <div className="rh-stars" aria-label="Five stars">★★★★★</div>
              <h3>Hundreds of <em>Happy</em> Customers</h3>
              <p>Real reviews from real customers — see what they have to say about their experience with us.</p>
              <a
                className="rh-button rh-button--red rh-review-summary__cta"
                href="#estimate"
              >
                Request a FREE Estimate
                <ArrowRight size={16} aria-hidden="true" />
              </a>
            </div>
            {testimonials.map((testimonial) => (
              <article key={testimonial.author}>
                <h3>{testimonial.title}</h3>
                <div className="rh-stars" aria-label="Five stars">★★★★★</div>
                <p>{testimonial.body}</p>
                <footer><span aria-hidden="true" /> {testimonial.author}</footer>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="rh-projects" id="projects">
        <div className="rh-container">
          <p className="rh-kicker">Projects</p>
          <h2 className="rh-section-title">Check Out Our Work Across the Michigan</h2>
          <p className="rh-section-lead">At Roach Home Improvement, we pride ourselves on delivering top-quality roofing solutions to homeowners</p>
          <div className="rh-project-grid">
            {projects.map((project, index) => (
              <button type="button" key={project.src} onClick={() => setLightbox(index)} aria-label={`Enlarge project ${index + 1}`}>
                <img src={project.thumb} alt={project.alt} loading="lazy" width="640" height="480" />
              </button>
            ))}
          </div>
          <p className="rh-tap-note">Tap images to enlarge</p>
        </div>
      </section>

      <section className="rh-why">
        <div className="rh-container">
          <h2 className="rh-section-title">Why Roach Home Stands Out in West Michigan</h2>
          <div className="rh-why__grid">
            {whyRoach.map(({ icon: Icon, title, text }) => (
              <article key={title}>
                <span className="rh-icon-circle"><Icon size={27} /></span>
                <h3>{title}</h3>
                <p>{text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="rh-roofing-services">
        <div className="rh-container">
          <p>Roofing Services</p>
          <h2 className="rh-section-title">Our Roofing Services in West Michigan</h2>
          <div className="rh-roofing-services__grid">
            {roofingServices.map((service, index) => (
              <article className={index === 0 ? "is-featured" : ""} key={service.title}>
                <span>{String(index + 1).padStart(2, "0")}</span>
                <h3>{service.title}</h3>
                <p>{service.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <div className="rh-centered-cta">
        <a className="rh-button rh-button--red" href="#estimate">Request a FREE Estimate</a>
      </div>

      <section className="rh-calculator" aria-label="Roofing cost calculator">
        <div className="rh-container">
          <div className="rh-calculator__frame">
            <RoofingCalculator />
          </div>
        </div>
      </section>

      <section className="rh-process">
        <div className="rh-container">
          <h2 className="rh-section-title">The Process is Simple and Straightforward With Us</h2>
          <div className="rh-process__grid">
            <div>
              <ol>
                {processSteps.map((step, index) => (
                  <li key={step.title}>
                    <span>{String(index + 1).padStart(2, "0")}</span>
                    <div><h3>{step.title}</h3><p>{step.text}</p></div>
                  </li>
                ))}
              </ol>
              <a className="rh-button rh-button--red" href="#estimate">Schedule a Free Consultation</a>
            </div>
            <img src={asset("roach-office-and-trucks-1200.webp")} alt="Roach Home Improvement office and trucks" loading="lazy" width="1200" height="900" />
          </div>
        </div>
      </section>

      <section className="rh-finance">
        <div className="rh-container rh-finance__layout">
          <div className="rh-finance__main">
            <h2>Financing Options Available in Michigan</h2>
            <p>We offer financing through Michigan Saves and Synchrony, with approval often taking minutes.</p>
            <h3>Michigan Saves</h3>
            <p>Loans up to $100,000, terms up to 15 years, no origination fees, no closing costs, no prepayment penalty.</p>
            <h3>Synchrony</h3>
            <p>Terms up to 20 years, with initial approval decisions in seconds.</p>
            <p>We also work with contractor financing programs offering 0% APR promotions for well-qualified buyers, and can walk you through home equity, personal loan, and credit options if those fit your situation better.</p>
            <Link className="rh-button rh-button--dark" to="/financing/">See Financing Options</Link>

            <h2>Roof Insurance Claims in Michigan</h2>
            <p>Storm damage from hail or high wind is usually covered by homeowners insurance. We document the damage, meet with your adjuster on-site, and handle supplemental items if the scope changes once repairs begin. You're responsible for your deductible and any upgrades beyond what the claim covers.</p>
            <div className="rh-insurance-list">
              {insuranceHighlights.map((item) => (
                <article key={item.title}>
                  <ShieldCheck size={22} aria-hidden="true" />
                  <div>
                    <strong>{item.title}</strong>
                    <p>{item.text}</p>
                  </div>
                </article>
              ))}
            </div>
          </div>

          <aside className="rh-finance__sidebar">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d5008.252615465319!2d-85.16092540847083!3d42.3342373366121!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x8817f1065847ed5f%3A0x39041b329c7ab36d!2s901%20Capital%20Ave%20NE%2C%20Battle%20Creek%2C%20MI%2049017%2C%20USA!5e0!3m2!1sen!2sam!4v1783509099897!5m2!1sen!2sam"
              title="Roach Home Improvement Battle Creek office map"
              loading="lazy"
              referrerPolicy="strict-origin-when-cross-origin"
              allowFullScreen
            />
            <article className="rh-offer-card">
              <img src={asset("roof-replacement-offer-600.webp")} alt="" loading="lazy" width="600" height="571" />
              <div>
                <strong>$500 OFF</strong>
                <h3>Full Roof Replacement</h3>
                <p>Plus 50% off gutters. Minimum 20 square roof. Cannot be combined with other promotions.</p>
                <a className="rh-button rh-button--dark rh-offer-card__button" href="#estimate">
                  Claim Offer <ArrowRight size={16} aria-hidden="true" />
                </a>
              </div>
            </article>
            <article className="rh-sidebar-card">
              <h3>Why Roach?</h3>
              <p>Highly rated roofing and home remodeling services</p>
              <ul>
                <li><ClipboardList /><span><b>No Hidden Costs, Free Quotes</b><small>Transparent, Up Front Pricing Without Surprises</small></span></li>
                <li><Users /><span><b>Family-Owned &amp; Operated</b><small>Michigan-Based Contractors</small></span></li>
                <li><Sparkles /><span><b>Experienced</b><small>20+ Years of Experience</small></span></li>
                <li><Smile /><span><b>Satisfaction Guarantee</b><small>We Offer a Satisfaction Guarantee on All Remodeling Work</small></span></li>
              </ul>
            </article>
            <article className="rh-sidebar-card rh-team-card">
              <h3>Meet the <em>Roach Team</em></h3>
              <p>The Roach Home Improvement Story</p>
              <div>
                {["team-shaun-roach.png", "team-terry-roach.png", "team-kelsey-cohoon.jpg", "team-rochelle-roach.png"].map((image) => (
                  <img src={asset(image)} alt="Roach Home Improvement team member" key={image} loading="lazy" />
                ))}
              </div>
              <p>At Roach Home Improvement, we pride ourselves on delivering top quality roofing solutions to homeowners in Battle Creek, Wyoming, Jackson, and surrounding areas.</p>
              <Link className="rh-button rh-button--dark" to="/about/">Meet the Roach Team</Link>
            </article>
            <article className="rh-sidebar-card">
              <h3>Talk to our experts</h3>
              <p>Free estimates, clear straightforward pricing</p>
              <a className="rh-button rh-button--dark" href="#estimate">Get Pricing</a>
            </article>
          </aside>
        </div>
      </section>

      <section className="rh-locations" id="locations">
        <div className="rh-container">
          <div className="rh-locations__head">
            <h2>Our 3 Headquarter Locations in West Michigan</h2>
            <p>We operate from 3 Michigan offices, in Battle Creek, Wyoming, and Jackson, so a local crew is never far from your home. Three locations mean faster response times, since a nearby team can reach your roof sooner than a company working from a single office. Each location handles roof repair, replacement, and home improvement services for its surrounding communities.</p>
          </div>

          <LocationsMap locations={locations} />

          <div className="rh-locations__cards">
            {locations.map((location) => (
              <article className="rh-location-card" key={location.city}>
                <h3>
                  {location.city}
                  {location.tag && <span>{location.tag}</span>}
                </h3>
                <address>
                  {location.address}
                  <br />
                  {location.detail}
                </address>
                {location.phone && (
                  <a
                    className="rh-location-card__phone"
                    href={`tel:${siteConfig.phoneE164(location.phone)}`}
                  >
                    <Phone size={15} aria-hidden="true" /> {location.phone}
                  </a>
                )}
                <div className="rh-location-card__links">
                  <a
                    className="rh-location-card__directions"
                    href={directionsUrl(location)}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Get directions <ArrowRight size={15} aria-hidden="true" />
                  </a>
                  <Link className="rh-location-card__page" to={location.page}>
                    View {location.city} roofing
                  </Link>
                </div>
              </article>
            ))}
          </div>

          <h2 className="rh-section-title">Some Communities We Serve in West Michigan</h2>
          <div className="rh-community-grid">
            {communities.map(([name, zip]) => {
              const page = communityPages[name];
              return (
                <div key={name}>
                  <CheckCircle2 size={20} />
                  <p>
                    {page ? (
                      <Link className="rh-community-link" to={page}>
                        <strong>{name}</strong>
                      </Link>
                    ) : (
                      <strong>{name}</strong>
                    )}
                    <span>{zip}</span>
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="rh-home-cta">
        <div className="rh-container rh-home-cta__inner">
          <img src={asset("homeowner-cta-background-1200.webp")} alt="Beautiful Michigan home" loading="lazy" width="1200" height="750" />
          <div>
            <h2>Ready to Transform Your Home?</h2>
            <p>Discover the difference that dedication and expertise make. Contact us today to schedule a consultation and take the first step towards a home that's as robust and welcoming as the communities we serve.</p>
            <a className="rh-button rh-button--red" href="#estimate">Get Started <ArrowRight size={16} /></a>
          </div>
        </div>
      </section>

      <section className="rh-faq">
        <div className="rh-container">
          <h2 className="rh-section-title">Frequently Asked Questions</h2>
          <div className="rh-faq__list">
            {faqs.map(([question, answer]) => (
              <details key={question}>
                <summary>{question}<span aria-hidden="true">−</span></summary>
                <p>{answer}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="rh-contact" id="estimate">
        <div className="rh-container rh-contact__grid">
          <article className="rh-contact-card">
            <h2>Get In Touch With Us</h2>
            <a href="mailto:office@roachhomeimprovement.com"><Mail /> office@roachhomeimprovement.com</a>
            <a href="tel:+12694206623"><Phone /> (269) 420-6623</a>
            <div className="rh-socials" aria-label="Social media">
              <span aria-label="Instagram">◎</span>
              <span aria-label="Facebook">f</span>
              <span aria-label="TikTok">♪</span>
              <span aria-label="X">X</span>
              <a href="https://www.youtube.com/watch?v=NZA-mxnVO0E" target="_blank" rel="noopener noreferrer" aria-label="YouTube">▶</a>
            </div>
          </article>
          <article className="rh-contact-form-card">
            <span className="rh-special-label">Online Special</span>
            <h2>Request a Free Roofing Estimate With Our Experts!</h2>
            <p>Request Free Pricing Today for Your Remodeling Needs!</p>
            <ContactForm hideLabels showPlaceholders grayInputBg />
          </article>
        </div>
      </section>

      <div className="rh-prefooter-band" aria-hidden="true" />

      {lightbox !== null ? (
        <div
          className="rh-lightbox"
          role="dialog"
          aria-modal="true"
          aria-label="Project gallery"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setLightbox(null);
          }}
        >
          <div className="rh-lightbox__dialog">
            <button
              className="rh-lightbox__close"
              type="button"
              onClick={() => setLightbox(null)}
              aria-label="Close project gallery"
              autoFocus
            >
              <X aria-hidden="true" />
            </button>
            <button
              className="rh-lightbox__previous"
              type="button"
              onClick={() => setLightbox((lightbox - 1 + projects.length) % projects.length)}
              aria-label="View previous project"
            >
              <ChevronLeft aria-hidden="true" />
            </button>
            <figure>
              <img src={projects[lightbox].src} alt={projects[lightbox].alt} />
              <figcaption>{lightbox + 1} / {projects.length}</figcaption>
            </figure>
            <button
              className="rh-lightbox__next"
              type="button"
              onClick={() => setLightbox((lightbox + 1) % projects.length)}
              aria-label="View next project"
            >
              <ChevronRight aria-hidden="true" />
            </button>
          </div>
        </div>
      ) : null}
    </main>
  );
}

export default RoachHomepage;
